/**
 * Lead Scoring Page
 * Visualizar e gerenciar scores de leads
 */

import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  resolveTemperature,
  temperatureRange,
  TEMPERATURE_LABELS,
  type LeadTemperature,
} from "@shared/lead-scoring";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Flame, Thermometer, Snowflake, TrendingUp, Users, Award, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

export default function LeadScoring() {
  const [, navigate] = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [editingPoints, setEditingPoints] = useState(0);

  // Queries
  const statsQuery = trpc.leadScoring.getStats.useQuery({});
  const leadsQuery = trpc.leadScoring.getLeadsWithScores.useQuery({ limit: 100, offset: 0 });

  useContextoDeLista({
    titulo: "Lead scoring",
    itens: ((leadsQuery.data as any)?.leads ?? (leadsQuery.data as any) ?? []).slice(0, 15).map((l: any) => ({
      contatoId: l.contactId ?? l.contact?.id,
      nome: l.contact?.name ?? null,
      pontos: l.totalScore ?? l.score ?? null,
      temperatura: l.temperature ?? null,
    })),
    extra: { estatisticas: statsQuery.data ?? null },
  });

  const hotLeadsQuery = trpc.leadScoring.getHotLeads.useQuery({});
  const warmLeadsQuery = trpc.leadScoring.getWarmLeads.useQuery({});
  const coldLeadsQuery = trpc.leadScoring.getColdLeads.useQuery({});
  const rulesQuery = trpc.leadScoring.getRules.useQuery({});
  const utils = trpc.useUtils();
  const updateRuleMutation = trpc.leadScoring.updateRule.useMutation({
    onSuccess: () => {
      utils.leadScoring.getRules.invalidate();
      setEditingRule(null);
    },
  });

  const createSegmentMutation = trpc.segments.create.useMutation({
    onSuccess: (seg: any) => {
      utils.segments.list.invalidate();
      toast.success(`Segmento "${seg?.name}" criado com sucesso! Acesse Segmentos para usar em campanhas.`);
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao criar segmento")),
  });

  // Condição por TEMPERATURA, não por faixa numérica solta: o limiar passa a
  // vir de um lugar só, e o segmento acompanha automaticamente se ele mudar.
  // (Antes o avaliador de segmentos nem conhecia `lead_score` e devolvia a
  // base inteira — o segmento "Hot Leads" casava 100% dos contatos.)
  const createAutoSegment = (type: LeadTemperature) => {
    const { min, max } = temperatureRange(type);
    const label = TEMPERATURE_LABELS[type];
    const description =
      type === "hot" ? `Pontuação a partir de ${min} — os mais engajados.`
      : type === "warm" ? `Pontuação entre ${min} e ${max}.`
      : `Pontuação abaixo de ${max + 1} — pouco engajados.`;

    createSegmentMutation.mutate({
      name: `Leads ${label}`,
      description,
      conditions: [{ field: "lead_temperature", operator: "is", value: type }] as any,
      matchType: "all",
    });
  };

  const stats = statsQuery.data;
  const leads = leadsQuery.data || [];
  const hotLeads = hotLeadsQuery.data || [];
  const warmLeads = warmLeadsQuery.data || [];
  const coldLeads = coldLeadsQuery.data || [];
  const rules = rulesQuery.data || [];

  // Filtrar leads por termo de busca
  const filteredLeads = useMemo(() => {
    if (!searchTerm) return leads;
    return leads.filter(
      (lead: any) =>
        lead.contact?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.contact?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [leads, searchTerm]);

  // Preparar dados para gráficos
  const distributionData = useMemo(() => {
    if (!stats) return [];
    const hot = temperatureRange("hot");
    const warm = temperatureRange("warm");
    return [
      { name: `${TEMPERATURE_LABELS.hot} (${hot.min}+)`, value: stats.distribution.hot, color: "#ef4444" },
      { name: `${TEMPERATURE_LABELS.warm} (${warm.min}-${warm.max})`, value: stats.distribution.warm, color: "#f59e0b" },
      { name: `${TEMPERATURE_LABELS.cold} (<${warm.min})`, value: stats.distribution.cold, color: "#3b82f6" },
    ];
  }, [stats]);

  const scoreRangeData = useMemo(() => {
    if (!leads || leads.length === 0) return [];
    
    const ranges = [
      { range: "0-10", count: 0 },
      { range: "11-25", count: 0 },
      { range: "26-50", count: 0 },
      { range: "51-75", count: 0 },
      { range: "76-100", count: 0 },
      { range: "100+", count: 0 },
    ];

    leads.forEach((lead: any) => {
      const score = lead.totalScore;
      if (score <= 10) ranges[0].count++;
      else if (score <= 25) ranges[1].count++;
      else if (score <= 50) ranges[2].count++;
      else if (score <= 75) ranges[3].count++;
      else if (score <= 100) ranges[4].count++;
      else ranges[5].count++;
    });

    return ranges;
  }, [leads]);

  // Cor e badge derivam da MESMA função do servidor e do ScoreBadge. Esta
  // página mantinha sua própria cópia dos limiares (`> 50` / `>= 25`), que
  // divergia da usada em getHotLeads.
  const getScoreColor = (score: number) =>
    ({ hot: "text-red-600", warm: "text-amber-600", cold: "text-blue-600" })[resolveTemperature(score)];

  const getScoreBadge = (score: number) => {
    const t = resolveTemperature(score);
    const style = {
      hot: { className: "bg-red-100 text-red-800", emoji: "🔥" },
      warm: { className: "bg-amber-100 text-amber-800", emoji: "🌡️" },
      cold: { className: "bg-blue-100 text-blue-800", emoji: "❄️" },
    }[t];
    return <Badge className={style.className}>{style.emoji} {TEMPERATURE_LABELS[t]}</Badge>;
  };

  return (
    <PermissionGuard permission="contacts:lead_scoring">
    <DashboardLayout>
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Lead Scoring</h1>
        <p className="text-gray-600 mt-2">
          Visualize e gerencie a pontuação automática de seus leads
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total de Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{stats?.totalLeads || 0}</div>
              <Users className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Score Médio</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{stats?.averageScore || 0}</div>
              <TrendingUp className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Score Máximo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{stats?.maxScore || 0}</div>
              <Award className="w-8 h-8 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Hot Leads</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold">{stats?.distribution.hot || 0}</div>
              <Flame className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="leads">Todos os Leads</TabsTrigger>
          <TabsTrigger value="hot">🔥 Hot Leads ({hotLeads.length})</TabsTrigger>
          <TabsTrigger value="warm">🌡️ Warm Leads ({warmLeads.length})</TabsTrigger>
          <TabsTrigger value="cold">❄️ Cold Leads ({coldLeads.length})</TabsTrigger>
          <TabsTrigger value="rules">Regras</TabsTrigger>
          <TabsTrigger value="segments">Segmentos</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Distribuição de Leads</CardTitle>
                <CardDescription>Leads por faixa de score</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={distributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {distributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Score Range Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Distribuição por Faixa</CardTitle>
                <CardDescription>Quantidade de leads em cada faixa de score</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={scoreRangeData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="range" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Scoring Rules */}
          <Card>
            <CardHeader>
              <CardTitle>O que está sendo pontuado</CardTitle>
              <CardDescription>Sinais que o sistema coleta hoje e quanto cada um vale</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Só os sinais com emissor. Os demais aparecem na aba Regras,
                    separados — mostrá-los aqui dava a impressão de que o
                    sistema coletava coisas que ninguém emite. */}
                {rules.filter((r: any) => r.emitted).map((rule: any) => (
                  <div key={rule.eventType} className="border rounded-lg p-3 text-center">
                    <div className="text-sm font-medium text-gray-600">{rule.label ?? rule.eventType}</div>
                    <div className={`text-2xl font-bold mt-1 ${rule.points < 0 ? "text-red-600" : "text-blue-600"}`}>
                      {rule.points > 0 ? `+${rule.points}` : rule.points}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">pontos</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* All Leads Tab */}
        <TabsContent value="leads">
          <Card>
            <CardHeader>
              <CardTitle>Todos os Leads</CardTitle>
              <CardDescription>Lista completa de leads com seus scores</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-sm"
                />
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Telefone</TableHead>
                      <TableHead className="text-right">Score</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.length > 0 ? (
                      filteredLeads.map((lead: any) => (
                        <TableRow key={lead.id}>
                          <TableCell className="font-medium">{lead.contact?.name || "-"}</TableCell>
                          <TableCell>{lead.contact?.email || "-"}</TableCell>
                          <TableCell>{lead.contact?.phoneNumber || "-"}</TableCell>
                          <TableCell className={`text-right font-bold ${getScoreColor(lead.totalScore)}`}>
                            {lead.totalScore}
                          </TableCell>
                          <TableCell>{getScoreBadge(lead.totalScore)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => lead.contact?.id && navigate(`/contacts/${lead.contact.id}`)}
                              disabled={!lead.contact?.id}
                            >
                              Ver Detalhes
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                          Nenhum lead encontrado
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hot Leads Tab */}
        <TabsContent value="hot">
          <LeadsTable leads={hotLeads} title="Hot Leads" description="Pontuação a partir de 50" onViewDetails={(id) => navigate(`/contacts/${id}`)} />
        </TabsContent>

        {/* Warm Leads Tab */}
        <TabsContent value="warm">
          <LeadsTable leads={warmLeads} title="Warm Leads" description="Pontuação entre 25 e 49" onViewDetails={(id) => navigate(`/contacts/${id}`)} />
        </TabsContent>

        {/* Cold Leads Tab */}
        <TabsContent value="cold">
          <LeadsTable leads={coldLeads} title="Cold Leads" description="Pontuação abaixo de 25" onViewDetails={(id) => navigate(`/contacts/${id}`)} />
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments">
          <Card>
            <CardHeader>
              <CardTitle>Segmentos Automaticos</CardTitle>
              <CardDescription>Crie segmentos baseados em score para usar em campanhas</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Hot Leads Segment */}
                <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <Flame className="w-6 h-6 text-red-500" />
                    <h3 className="font-semibold">Hot Leads</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Score acima de 50 (Altamente engajados)</p>
                  <div className="mb-4 p-3 bg-red-50 rounded">
                    <div className="text-2xl font-bold text-red-600">{hotLeads.length}</div>
                    <div className="text-xs text-gray-600">leads nesta categoria</div>
                  </div>
                  <Button
                    className="w-full"
                    variant="default"
                    size="sm"
                    disabled={createSegmentMutation.isPending}
                    onClick={() => createAutoSegment("hot")}
                  >
                    {createSegmentMutation.isPending ? "Criando..." : "Criar Segmento"}
                  </Button>
                </div>

                {/* Warm Leads Segment */}
                <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <Thermometer className="w-6 h-6 text-orange-500" />
                    <h3 className="font-semibold">Warm Leads</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Score 25-50 (Moderadamente engajados)</p>
                  <div className="mb-4 p-3 bg-orange-50 rounded">
                    <div className="text-2xl font-bold text-orange-600">{warmLeads.length}</div>
                    <div className="text-xs text-gray-600">leads nesta categoria</div>
                  </div>
                  <Button
                    className="w-full"
                    variant="outline"
                    size="sm"
                    disabled={createSegmentMutation.isPending}
                    onClick={() => createAutoSegment("warm")}
                  >
                    {createSegmentMutation.isPending ? "Criando..." : "Criar Segmento"}
                  </Button>
                </div>

                {/* Cold Leads Segment */}
                <div className="border rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <Snowflake className="w-6 h-6 text-blue-500" />
                    <h3 className="font-semibold">Cold Leads</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Score abaixo de 25 (Baixo engajamento)</p>
                  <div className="mb-4 p-3 bg-blue-50 rounded">
                    <div className="text-2xl font-bold text-blue-600">{coldLeads.length}</div>
                    <div className="text-xs text-gray-600">leads nesta categoria</div>
                  </div>
                  <Button
                    className="w-full"
                    variant="outline"
                    size="sm"
                    disabled={createSegmentMutation.isPending}
                    onClick={() => createAutoSegment("cold")}
                  >
                    {createSegmentMutation.isPending ? "Criando..." : "Criar Segmento"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rules Tab */}
        <TabsContent value="rules">
          <Card>
            <CardHeader>
              <CardTitle>Gerenciar Regras de Scoring</CardTitle>
              <CardDescription>Clique em Editar para alterar os pontos de cada evento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {rules.map((rule: any) => (
                  <div key={rule.eventType} className={`flex items-center justify-between border rounded-lg p-4 ${rule.emitted ? "" : "opacity-60"}`}>
                    <div className="flex-1 min-w-0 mr-4">
                      <div className="font-medium flex items-center gap-2">
                        {rule.label ?? rule.eventType}
                        {/* Sinaliza as regras herdadas que ainda não têm nada
                            emitindo. Antes elas apareciam iguais às demais: o
                            usuário ajustava os pontos e nada acontecia. */}
                        {!rule.emitted && (
                          <Badge variant="outline" className="text-[10px] font-normal">
                            ainda não coletado
                          </Badge>
                        )}
                      </div>
                      {rule.description && (
                        <div className="text-sm text-gray-600">{rule.description}</div>
                      )}
                    </div>

                    {editingRule === rule.eventType ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          className="w-24 text-center font-bold"
                          value={editingPoints}
                          onChange={(e) => setEditingPoints(Number(e.target.value))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") updateRuleMutation.mutate({ eventType: rule.eventType, points: editingPoints });
                            if (e.key === "Escape") setEditingRule(null);
                          }}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          onClick={() => updateRuleMutation.mutate({ eventType: rule.eventType, points: editingPoints })}
                          disabled={updateRuleMutation.isPending}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingRule(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-4">
                        <div className="text-2xl font-bold w-16 text-right">{rule.points}</div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setEditingRule(rule.eventType); setEditingPoints(rule.points); }}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1" />
                          Editar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
    </DashboardLayout>
    </PermissionGuard>
  );
}

/**
 * Componente reutilizável para tabela de leads
 */
function LeadsTable({ leads, title, description, onViewDetails }: { leads: any[]; title: string; description: string; onViewDetails: (contactId: number) => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length > 0 ? (
                leads.map((lead: any) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">{lead.contact?.name || "-"}</TableCell>
                    <TableCell>{lead.contact?.email || "-"}</TableCell>
                    <TableCell>{lead.contact?.phoneNumber || "-"}</TableCell>
                    <TableCell className="text-right font-bold">{lead.totalScore}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => lead.contact?.id && onViewDetails(lead.contact.id)}
                        disabled={!lead.contact?.id}
                      >
                        Ver Detalhes
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                    Nenhum lead encontrado nesta categoria
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
